// @flow

import get from 'lodash/get';
import React, { useContext, useState, useRef, Fragment } from 'react';
import queryString from 'query-string';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { useRouteMatch, useHistory } from 'react-router-dom';
import md5 from 'md5';
// wrapperPaddings are LESS variables for wrapper needed for iniital calucation of grid columns width
import { Section, Wrapper, wrapperPaddings } from '@riseart/layout';
import { Link, MediaQuery } from '@riseart/common';
import { isTouchDevice } from '@riseart/fe-utils';
import { SSRContext } from 'shared_data/providers/ssr/Context';
import { useUrlParams } from 'shared_data/providers/url/useUrlParams';
import { NavigatorService } from 'shared_services/riseart/Navigator';
import { LayoutDefault } from 'shared_components/layouts/Default';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { SimilarArtworksLink } from 'shared_components/arts/widgets/SimilarArtworksLink';
import { HOCArtsGrid } from 'shared_hocs/arts/Grid';
import { HOCFilterContainer } from 'shared_hocs/filters/Container';
import { MetaCategoryContainer } from 'shared_hocs/meta/categories/Category';
import { ArtListSection } from 'shared_components/arts/containers/sections/List';
import { ArtListFilterContainer } from 'shared_components/arts/containers/filter/List';
import { FilterSection } from 'shared_components/common/filters/container/Section';
import HOCFilterPageRoute from 'shared_hocs/routes/FilterPageRoute';
import COMBINE_ART_SEARCH_AND_CATEGORY_QUERY from 'shared_data/queries/art/combinedSearchAndCategory.graphql';
import SEARCH_ART_QUERY from 'shared_data/queries/search/art.graphql';
import VISUAL_ART_QUERY from 'shared_data/queries/visual/art.graphql';
import VISUAL_SELECTION_QUERY from 'shared_data/queries/visual/selection.graphql';
import LENS_ART_QUERY from 'shared_data/queries/art/lens.graphql';
import {
  pagination as PAGINATION_ENUM,
  filter as FILTER_ENUM,
  analytics as ANALYTICS_ENUM,
  search as ENUM_SEARCH,
} from 'Enum';
import { GOOGLE_TAGMANAGER_ACTIONS } from 'shared_data/providers/google/tagmanager/Actions';
import {
  actionFactory,
  convertPayloadToQueryVars,
  filterSearchTextRouteHandler,
} from 'shared_services/riseart/utils/Utils';
import { LocationManager } from 'shared_services/riseart/url/Location';
import { VisualSearch } from 'shared_components/forms/search/visual/Visual';
import { LensSection } from 'shared_components/forms/search/lens/Section';
import { SearchForm } from 'shared_components/forms/search/Form';
import { WithFilterState } from 'shared_hocs/connect/WithFilterState';
import { FilterBreadcrumbs } from 'shared_components/common/filters/breadcrumbs/Breadcrumbs';
import { useApplicationMessages, DEVICE } from 'shared_hocs/messages/application/hooks';

import { artListFilterSectionCls } from 'shared_components/pages/art/List.less';
import { searchFormSectionCls } from 'shared_components/forms/search/Form.less';
import { similarArtworkslLinkCls } from 'shared_components/arts/widgets/SimilarArtworksLink.less';

import {
  raScreenMd,
  raScreenMdMax,
  raScreenLg,
} from '@riseart/antd-provider/dist/website/variables.less';

// HOCs
const ArtsGrid = HOCArtsGrid(ArtListSection, { domain: FILTER_ENUM.domain.ART });
const HOCArtsFiltersContainer = HOCFilterContainer(ArtListFilterContainer, {
  domain: FILTER_ENUM.domain.ART,
  loadIndexedUrlsList: true,
});

const ArtList = ({
  domain,
  pageUrl,
  paginationType,
  location,
  isMobile,
  isTablet,
  isDesktop,
}: Object) => {
  const history = useHistory();
  const { isSSR } = useContext(SSRContext);
  const dispatch = useDispatch();
  const { q, v, l, page: _page, ...restQueryParams } = queryString.parse(location.search);
  const hasTextQuery = q !== undefined;
  const showVisualSearch = v !== undefined;
  const showLens = l !== undefined;
  const hasVisualSearchQuery = !!v;
  const hasLensSearchQuery = !!l;
  const lensImageId: string | number | null = l || null;
  const currentJourney = useRef();
  const [journey, setJourney] = useState();
  const { getFlatMessages } = useApplicationMessages({
    location,
    messageLocation: 'shopPage',
    device:
      (isMobile && DEVICE.MOBILE) ||
      (isTablet && DEVICE.TABLET) ||
      (isDesktop && DEVICE.DESKTOP) ||
      null,
  });

  // reset currentJourney if v has falsey value
  if (currentJourney.current && !v) {
    currentJourney.current = null;
  }

  /**
   * assembleVisualSearchUrl
   *
   * @param {string} v
   * @returns {string}
   */
  function assembleVisualSearchUrl(v: string): string {
    const urlOptions = UrlAssembler.mergeOptions({ search: { ...restQueryParams, v } });

    return `${location.pathname}${urlOptions.search}${location.hash}`;
  }

  /**
   * assembleLensSearchUrl
   *
   * @param {string} l
   * @returns {string}
   */
  function assembleLensSearchUrl(l: string): string {
    const urlOptions = UrlAssembler.mergeOptions({ search: { ...restQueryParams, l } });

    return `${location.pathname}${urlOptions.search}${location.hash}`;
  }

  /**
   * handleGoToResults
   *
   * @param {Object | null} journey
   * @returns {void}
   */
  function handleGoToResults(journey: Object | null): void {
    if (journey && journey.item && journey.item.id !== v) {
      history.push(assembleVisualSearchUrl(journey.item.id));
    }
    setJourney(journey);
    currentJourney.current = journey;
  }

  // Define fetch query based on type of search
  let FETCH_QUERY = (isSSR && COMBINE_ART_SEARCH_AND_CATEGORY_QUERY) || SEARCH_ART_QUERY;
  if (hasVisualSearchQuery) {
    FETCH_QUERY = VISUAL_ART_QUERY;
  } else if (hasLensSearchQuery) {
    FETCH_QUERY = LENS_ART_QUERY;
  }

  return (
    <LayoutDefault>
      <ArtsGrid
        includeLDJSON
        lazyloadAfter={10}
        hideSort={hasVisualSearchQuery || hasLensSearchQuery}
        isSSR={isSSR}
        fetchQuery={FETCH_QUERY}
        onLoadData={(queryResponse) => {
          const responseJourney = get(queryResponse, 'visualArt.journey');

          if (
            (!currentJourney.current && responseJourney && responseJourney.journeyId) ||
            (responseJourney &&
              currentJourney.current &&
              responseJourney.journeyId !== currentJourney.current.journeyId)
          ) {
            currentJourney.current = responseJourney;
          }
        }}
        isSsrQuery
        promotionalCards={
          // Only show promotional cards if pagination type is infinite, which meants it is not a bot viewing the page
          paginationType === PAGINATION_ENUM.type.INFINITE
            ? getFlatMessages().filter(({ show }) => show)
            : null
        }
        itemsPerPage={hasVisualSearchQuery ? 90 : null}
        domain={domain}
        wrapperPaddings={wrapperPaddings}
        pageUrl={pageUrl}
        paginationType={paginationType}
        showAddToCart
        showHoverContent={!isTouchDevice()}
        sendVisitorId={false}
        noItems={
          <FormattedMessage
            id="components.art.gridNoArtworks"
            values={{
              p: (chunks) => <p>{chunks}</p>,
              strong: (chunks) => <strong>{chunks}</strong>,
              a: (chunks) => (
                <FormattedMessage id="components.art.gridNoArtworksLinkTitle">
                  {(linkTitle: string) => (
                    <Link
                      obfuscate
                      rel="nofollow"
                      to={UrlAssembler.artistList({ search: { q: q || '' } })}
                      title={linkTitle}
                    >
                      {chunks}
                    </Link>
                  )}
                </FormattedMessage>
              ),
            }}
          >
            {(noItemsText: string) => noItemsText}
          </FormattedMessage>
        }
        tracking={{
          name: ANALYTICS_ENUM.list.name.ART_GRID,
          enabled: true,
          enableInitial: true,
          enableClick: true,
        }}
        additionalFilterQueryParams={({ visitorId, shippingCountryCode, storeCode, filters }) => ({
          ...(!hasVisualSearchQuery && shippingCountryCode
            ? { rankingCountry: shippingCountryCode }
            : null),
          ...(isSSR
            ? {
                inputMetaCategoryFilter: {
                  domain,
                  store: storeCode,
                  ...convertPayloadToQueryVars(
                    { referrer: LocationManager.get('uri'), ...filters },
                    storeCode,
                  ),
                },
              }
            : null),
          ...(q && !showVisualSearch && !showLens ? { textQuery: q } : null),
          ...(hasLensSearchQuery
            ? {
                inputImage: {
                  imageId: lensImageId,
                  analytics: {
                    visitorId,
                    pageType: ANALYTICS_ENUM.page.type.TYPE_PLP,
                    ...(visitorId ? { anonymousId: md5(visitorId) } : null),
                  },
                },
              }
            : null),
          ...((hasVisualSearchQuery && {
            journey: {
              artId: v,
              analytics: {
                visitorId,
                pageType: ANALYTICS_ENUM.page.type.TYPE_PLP,
                ...(visitorId ? { anonymousId: md5(visitorId) } : null),
              },
              ...((currentJourney.current && currentJourney.current.journeyId) ||
              (journey && journey.journeyId)
                ? {
                    journeyId:
                      (currentJourney &&
                        currentJourney.current &&
                        currentJourney.current.journeyId) ||
                      null,
                    refresh:
                      (currentJourney &&
                        currentJourney.current &&
                        currentJourney.current.refresh) ||
                      false,
                  }
                : null),
            },
          }) ||
            null),
        })}
        additionalActionControls={(artData: Object) =>
          artData.isProduct !== false ? (
            <SimilarArtworksLink
              url={assembleVisualSearchUrl(artData.id)}
              className={similarArtworkslLinkCls}
              onClick={() => {
                dispatch(
                  actionFactory(GOOGLE_TAGMANAGER_ACTIONS.VISUAL_SEARCH_ADD, {
                    trigger: ANALYTICS_ENUM.visualSearch.trigger.ARTWORK_CATEGORY,
                    artwork: artData,
                  }),
                );
              }}
            />
          ) : null
        }
      >
        {({ data, loading }) => (
          <Fragment>
            {hasTextQuery || showVisualSearch || showLens ? (
              <Section background="white" padding="none" className={searchFormSectionCls}>
                <Wrapper>
                  <WithFilterState domain={domain}>
                    {(filters) => (
                      <FilterBreadcrumbs
                        filterDomain={domain}
                        filters={filters}
                        config={['medium', 'subMedium', 'style', 'subject', 'subSubject']}
                      />
                    )}
                  </WithFilterState>
                  {showVisualSearch ? (
                    <VisualSearch
                      title={
                        <FormattedMessage id="forms.search.visual.yourSearchResults">
                          {(title: string) => title}
                        </FormattedMessage>
                      }
                      journey={!loading && data && data.visualArt && data.visualArt.journey}
                      onGoToResults={handleGoToResults}
                      urlAssembler={assembleVisualSearchUrl}
                    />
                  ) : null}
                  {showLens ? (
                    <LensSection
                      title={
                        <FormattedMessage id="forms.search.lens.yourSearchResults">
                          {(title: string) => title}
                        </FormattedMessage>
                      }
                      initialImageId={lensImageId ? parseInt(lensImageId, 10) : null}
                      loading={loading}
                      assembleUrl={assembleLensSearchUrl}
                    />
                  ) : null}
                  {hasTextQuery ? (
                    <SearchForm
                      allowTracking
                      title={
                        <FormattedMessage id="forms.search.yourArtworkSearchResults">
                          {(title: string) => title}
                        </FormattedMessage>
                      }
                      urlRouteHandler={filterSearchTextRouteHandler}
                      enableClear
                      showSuggestions
                      autoFocus
                      excludedParameters={['page']}
                      displayStyle="horizontal"
                      domain={ENUM_SEARCH.domain.ART}
                    />
                  ) : null}
                </Wrapper>
              </Section>
            ) : null}
            <FilterSection
              topPadded={hasTextQuery || showVisualSearch || showLens}
              className={artListFilterSectionCls}
            >
              {!hasTextQuery && !showVisualSearch && !showLens ? (
                <MetaCategoryContainer
                  domain={domain}
                  config={['medium', 'subMedium', 'style', 'subject', 'subSubject']}
                />
              ) : null}
              <HOCArtsFiltersContainer
                domain={domain}
                additionalQueryParams={q ? { textQuery: q } : null}
                fetchQuery={v ? VISUAL_SELECTION_QUERY : null}
              />
            </FilterSection>
          </Fragment>
        )}
      </ArtsGrid>
    </LayoutDefault>
  );
};

const HOCArtListPage = HOCFilterPageRoute(ArtList, FILTER_ENUM.domain.ART);

export default function ArtListPage(props: any) {
  const { INFINITE, SLIDER } = PAGINATION_ENUM.type;
  const match = useRouteMatch();
  const { translatedLocation: location } = useUrlParams();

  return (
    <MediaQuery minWidth={raScreenMd} maxWidth={raScreenMdMax}>
      {(isTablet) => (
        <MediaQuery minWidth={raScreenLg}>
          {(isDesktop) => (
            <HOCArtListPage
              isMobile={!isTablet && !isDesktop}
              isTablet={isTablet}
              isDesktop={isDesktop}
              {...props}
              match={match}
              location={location}
              paginationType={NavigatorService.isBot() ? SLIDER : INFINITE}
              domain={FILTER_ENUM.domain[props.routeConfig.filterDomainKey]}
            />
          )}
        </MediaQuery>
      )}
    </MediaQuery>
  );
}
